import React, { useContext } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Hidden from '@material-ui/core/Hidden'
import { useAbility } from 'currentContext'
import EntityContext from 'components/item/context'
import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  required: {
    color: theme.palette.error.light
  },
  label: {
    color: theme.palette.inputs.label.color.regular,
    fontWeight: 600,
    width: '30%'
  },
  componentWrapper: {
    width: '70%',
    '& .MuiInputLabel-root, & .MuiOutlinedInput-notchedOutline > legend > span, & .MuiFormControlLabel-label': {
      display: 'none'
    },
    '& .MuiFormControl-root': {
      '& .MuiFormLabel-root': {
        display: 'none'
      },
      '& .MuiFormControlLabel-label': {
        display: 'block'
      }
    }
  },
  mobileBox: {
    margin: theme.spacing(1, 0, 0.5),
    width: '100%',
    padding: theme.spacing(0.75, 1.5),
    color: theme.palette.grey[900],
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1
  },
  mobileLabel: {
    color: theme.palette.grey[400],
    fontWeight: 400,
    fontSize: '0.857rem',
    display: 'block'
  },
  mobileValue: {
    color: theme.palette.grey[900],
    fontWeight: 600,
    fontSize: '0.929rem',
    '&:empty::after': {
      content: '"-"'
    },
    '& .MuiFormLabel-root': {
      display: 'none'
    }
  },
  labelVerticalAlign: {
    verticalAlign: 'top',
    paddingTop: '7.5px !important'
  }
}))

interface PropsWithLabelAndRequired {
  label?: React.ReactNode
  required?: boolean
  [x: string]: any
}

export function withTableWrapper<P extends { [x: string]: any }>(
  Component: React.ComponentType<P>,
  getProps?: (props: P) => PropsWithLabelAndRequired | undefined,
  options?: {
    alignLabelToTop?: boolean
  }
): React.ComponentType<P> {
  // eslint-disable-next-line react/display-name
  return (props: React.PropsWithChildren<P>) => {
    const classes = useStyles()
    const ability = useAbility()
    const context = useContext(EntityContext)

    const { label, required } = getProps !== undefined ? getProps(props) ?? { label: '', required: false } : (props as PropsWithLabelAndRequired)

    const items: Array<{ name: string | string[] }> = props.name !== undefined ? [{ name: props.name }] : props.items

    if (items !== undefined) {
      let canRead = false
      let canUpdate = false

      items.forEach((item) => {
        if (Array.isArray(item.name)) {
          item.name.forEach((name) => {
            if (ability.can('read', context.authorizationSubject, name)) {
              canRead = true
            }
            if (ability.can('update', context.authorizationSubject, name)) {
              canUpdate = true
            }
          })
        } else {
          if (ability.can('read', context.authorizationSubject, item.name)) {
            canRead = true
          }
          if (ability.can('update', context.authorizationSubject, item.name)) {
            canUpdate = true
          }
        }
      })

      if (!canRead && !canUpdate) {
        return null
      }
    }

    return (
      <>
        <Hidden mdDown>
          <tr>
            <td
              className={clsx(classes.label, {
                [classes.labelVerticalAlign]: options?.alignLabelToTop === true
              })}
            >
              {label}
              {Boolean(required) && <abbr className={classes.required}>&nbsp;*</abbr>}
            </td>
            <td className={classes.componentWrapper}>
              <Component {...props} />
            </td>
          </tr>
        </Hidden>
        <Hidden lgUp>
          <div className={classes.mobileBox}>
            <span className={classes.mobileLabel}>{label}</span>
            <div className={classes.mobileValue}>
              <Component {...props} />
            </div>
          </div>
        </Hidden>
      </>
    )
  }
}

export default withTableWrapper
